import React, { useMemo } from "react";
import { usePlayerModal } from "../PlayerModalContext";
import { lookUpPlayerName } from "../playerDisplayName";
import { mainSectionLeaderNames } from "../sectionLeaders";
import { PickRoundMeta } from "./PickRoundMeta";
import { PlayerAvatarWithScore } from "./PlayerAvatarWithScore";
import { PlayerPickTrigger } from "./PlayerPickTrigger";
import { SectionLeaderNamePill } from "./SectionLeaderNamePill";
import { MissedCutPickCardShell } from "./MissedCutPickCardShell";
import {
    MISSED_CUT_CARD_MIN_HEIGHT_CLASS,
    MISSED_CUT_META_RESERVE_CLASS,
    PickRowAvatarPlaceholder,
} from "./PickRowAvatarPlaceholder";

export function RankingsSection({ rankings, formatter, cutPickNames = [] }) {
    const { openPlayer } = usePlayerModal();
    const leaderSet = useMemo(
        () => new Set(mainSectionLeaderNames(rankings)),
        [rankings],
    );
    const cutPickSet = useMemo(
        () => new Set(cutPickNames),
        [cutPickNames],
    );

    return (
        <section
            aria-label="Rankings"
            className="rankings rounded-xl border border-masters-moss/30 bg-masters-cream/95 p-4 text-masters-forest shadow-lg shadow-black/20 sm:p-6"
        >
            <h2 className="mb-4 border-b border-masters-parchment pb-2 text-xs font-semibold uppercase tracking-[0.2em] text-masters-goldmuted">
                Rankings
            </h2>
            {rankings.map((person) => (
                <div key={person.name} className="mb-6 last:mb-0">
                    <div className="flex flex-row items-center justify-between gap-4 border-b border-masters-parchment/80 pb-3 pt-1">
                        <div className="min-w-0 pl-1">
                            {leaderSet.has(person.name) ? (
                                <SectionLeaderNamePill name={person.name} />
                            ) : (
                                <span className="text-base font-semibold">
                                    {person.name}
                                </span>
                            )}
                        </div>
                        <div className="text-base font-semibold text-masters-moss">
                            {formatter.format(Math.floor(person.totalMoney))}
                        </div>
                    </div>
                    <div className="space-y-1 pl-1 pt-2 text-sm">
                        {person.picks.map((pick) => {
                            const summ = person.summary?.find(
                                ({ name }) => name === pick,
                            );
                            if (!summ && cutPickSet.has(pick)) {
                                return (
                                    <div key={`${person.name}-${pick}`}>
                                        <div className="pb-2">
                                            <MissedCutPickCardShell>
                                                <div
                                                    className={`flex items-center gap-2 rounded-lg bg-masters-parchment/40 px-2 py-2 ${MISSED_CUT_CARD_MIN_HEIGHT_CLASS}`}
                                                >
                                                    <PlayerPickTrigger
                                                        ariaLabel={`Player details: ${pick}`}
                                                        onOpen={() =>
                                                            openPlayer(pick)
                                                        }
                                                    >
                                                        <PickRowAvatarPlaceholder />
                                                    </PlayerPickTrigger>
                                                    <div className="flex min-h-0 min-w-0 flex-1 flex-col justify-center pl-1">
                                                        <PlayerPickTrigger
                                                            ariaLabel={`Player details: ${pick}`}
                                                            onOpen={() =>
                                                                openPlayer(pick)
                                                            }
                                                        >
                                                            <div className="font-medium text-masters-forest">
                                                                {pick}
                                                            </div>
                                                        </PlayerPickTrigger>
                                                        <div className="mt-0.5 text-sm text-masters-moss">
                                                            {formatter.format(0)}
                                                        </div>
                                                        <div
                                                            className={`mt-0.5 ${MISSED_CUT_META_RESERVE_CLASS}`}
                                                        >
                                                            <PickRoundMeta
                                                                className="mt-0"
                                                                thru={null}
                                                                roundGrossStrokes={
                                                                    undefined
                                                                }
                                                                recentVsPar={
                                                                    null
                                                                }
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </MissedCutPickCardShell>
                                        </div>
                                    </div>
                                );
                            }
                            if (!summ) {
                                return (
                                    <div
                                        key={`${person.name}-${pick}`}
                                        className="rounded-lg px-2 py-1 text-masters-goldmuted"
                                    >
                                        <PlayerPickTrigger
                                            ariaLabel={`Player details: ${pick}`}
                                            onOpen={() => openPlayer(pick)}
                                        >
                                            <span className="line-through decoration-masters-goldmuted/60">
                                                {pick}
                                            </span>
                                        </PlayerPickTrigger>
                                    </div>
                                );
                            }
                            return (
                                <div key={`${person.name}-${pick}`}>
                                    <div className="pb-2">
                                        <div className="flex items-center gap-2 rounded-lg bg-masters-parchment/40 px-2 py-2">
                                            <PlayerPickTrigger
                                                ariaLabel={`Player details: ${pick}`}
                                                onOpen={() => openPlayer(pick)}
                                            >
                                                <PlayerAvatarWithScore
                                                    src={summ.avatarURL}
                                                    alt=""
                                                    scoreVsPar={
                                                        summ.scoreVsPar
                                                    }
                                                    imgClassName="h-10 w-10"
                                                />
                                            </PlayerPickTrigger>
                                            <div className="min-w-0 pl-1">
                                                <PlayerPickTrigger
                                                    ariaLabel={`Player details: ${pick}`}
                                                    onOpen={() =>
                                                        openPlayer(pick)
                                                    }
                                                >
                                                    <div className="font-medium text-masters-forest">
                                                        {lookUpPlayerName(
                                                            person,
                                                            pick,
                                                        )}
                                                    </div>
                                                </PlayerPickTrigger>
                                                <div className="mt-0.5 text-sm text-masters-moss">
                                                    {formatter.format(
                                                        summ.amount,
                                                    )}
                                                </div>
                                                <PickRoundMeta
                                                    className="mt-0.5"
                                                    thru={summ.roundThruLabel}
                                                    roundGrossStrokes={
                                                        summ.completedRoundGross
                                                    }
                                                    recentVsPar={
                                                        summ.recentThreeVsPar
                                                    }
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            ))}
        </section>
    );
}
